import { useMemo, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq, getSession } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { navigate, setQuery, useRoute } from "../lib/router";
import { useSession } from "../lib/session";
import { LEAD_AREAS, type Area } from "../lib/refdata";
import { useLog } from "../components/QuickLog";
import { TargetDials } from "../components/Dials";
import { PageHead } from "../components/Shell";
import { ErrorBox, Loading, Panel, Seg, useToast } from "../components/ui";
import { openPerson } from "../components/PersonDrawer";
import { ThisWeek } from "./WeeklyReview";
import { GxPanel } from "../components/Gx";
import { BdayRow, useBirthdays } from "./Birthdays";
import { GoalsPanel } from "../components/Goals";
import { DoItSheet, ExtendedButton, canDo, type DoItem } from "../components/DoIt";
import { NextStepSheet } from "../components/NextStep";
import { BookMeetingButton } from "../components/Appointments";
import { TaskRow, TaskSheet, lateBy, useAssignees, useMyTasks, type Task } from "../components/Tasks";

export type CCItem = {
  item_key: string; area: string; level: "red" | "amber" | "info"; kind: string; title: string; why: string;
  person_id: string | null; link: string | null; due: string | null; days_late: number | null; n: number | null;
  owner_user_id: string | null; owner_role: string | null; owner_name: string | null; escalated: boolean;
};

/** Shared with the banner on every page. */
export const useCommandCenter = (scope: "mine" | "all" = "mine") => {
  const { session } = useSession();
  return useQuery(session ? `cc:items:${scope}:${dayjs().format("YYYY-MM-DD")}` : null, () => db.rpc<CCItem[]>("command_center", { p_scope: scope }), { staleMs: 60_000 });
};

const AREA: Record<string, string> = {
  team: "Team", clients: "Clients", pipeline: "Pipeline", meetings: "Meeting", today: "Your day", tasks: "Task",
  warm: "Warm market", networking: "People met", hnw: "HNW", travel: "Travel", new_connections: "Outreach", other: "Leads", leads: "Leads",
};
const TOP = 5;      // rows shown in the Today list before "View all"
const SIDE = 3;     // rows shown in a side panel before "Show all"

function Late({ n }: { n: number | null }) {
  if (n == null) return null;
  if (n <= 0) return <span className="pill cooling">{n === 0 ? "Today" : `In ${-n}d`}</span>;
  return <span className={clsx("pill", n >= 3 ? "alert" : "cooling")}>{n === 1 ? "1 day late" : `${n} days late`}</span>;
}

export default function CommandCenter() {
  const { session, profile, can } = useSession();
  const me = session?.user.id;
  const toast = useToast();
  const log = useLog();
  const { query } = useRoute();
  const boss = profile?.role_key === "owner" || profile?.role_key === "admin";
  const scope = (boss && query.get("scope") === "all" ? "all" : "mine") as "mine" | "all";
  const cc = useCommandCenter(scope);
  const loggedToday = useQuery(`actlog:today:${dayjs().format("YYYY-MM-DD")}`, async () =>
    (await db.selectCount("activities", { select: "id", occurred_on: `eq.${dayjs().format("YYYY-MM-DD")}`, logged_by: `eq.${getSession()?.user.id}`, limit: 1 })).total).data;
  const tasks = useMyTasks();
  const people = useAssignees();
  const today = dayjs().format("YYYY-MM-DD");
  const score = useQuery(can("today") ? `today:score:${today}` : null, () =>
    db.rpc<any[]>("daily_scorecard", { p_from: today, p_to: today, p_user: me }).then((r) => r[0]));
  const [openTask, setOpenTask] = useState<Task | null | "new">(null);
  const [taskFrom, setTaskFrom] = useState<CCItem | null>(null);
  const [tab, setTab] = useState<"priorities" | "due" | "carried">("priorities");
  const [full, setFull] = useState(false);       // the Today list: a handful, or all
  const [more, setMore] = useState<Record<string, boolean>>({});   // side panels: 3, or all
  const bdays = useBirthdays(0);
  const [doIt, setDoIt] = useState<DoItem | null>(null);
  const [nextFor, setNextFor] = useState<string | null>(null);

  // Leads today owns contacting leads: their follow-ups and commitments live there, not here
  const isLeadContact = (i: CCItem) => (i.kind === "follow_up" || i.kind === "commitment") && ((LEAD_AREAS as string[]).includes(i.area) || i.area === "other");
  const leadContacts = (cc.data ?? []).filter(isLeadContact);
  const leadLate = leadContacts.filter((i) => (i.days_late ?? 0) > 0).length;
  const items = (cc.data ?? []).filter((i) => !isLeadContact(i));
  const t = tasks.data ?? [];
  const taskLevel = (x: Task) => {
    const l = lateBy(x);
    if (l == null) return x.priority === "Urgent" ? "amber" : null;
    if (l >= 3 || (l >= 0 && x.priority === "Urgent")) return "red";
    if (l >= 0) return "amber";
    return null;
  };
  const redTasks = t.filter((x) => taskLevel(x) === "red");
  const amberTasks = t.filter((x) => taskLevel(x) === "amber");
  const laterTasks = t.filter((x) => !taskLevel(x)).sort((a, b) => (a.due_on ?? "9999").localeCompare(b.due_on ?? "9999"));
  const red = items.filter((i) => i.level === "red").sort((a, b) => (b.days_late ?? 0) - (a.days_late ?? 0));
  // birthdays have their own panel at the side (under Today's meetings)
  const amberAll = items.filter((i) => i.level === "amber" && i.kind !== "summary" && i.kind !== "debt" && i.kind !== "birthday");
  const birthdays = items.filter((i) => i.kind === "birthday");
  const debts = items.filter((i) => i.kind === "debt");
  const summaries = items.filter((i) => i.kind === "summary");
  // meetings in time order; ones already started drop to the bottom
  const mins = (i: CCItem) => { const m = /^(\d{1,2}):(\d{2}) (AM|PM)/.exec(i.why ?? ""); return m ? ((Number(m[1]) % 12) + (m[3] === "PM" ? 12 : 0)) * 60 + Number(m[2]) : 0; };
  const nowMin = dayjs().hour() * 60 + dayjs().minute();
  const meetings = items.filter((i) => i.level === "info").sort((a, b) => Number(mins(a) < nowMin - 30) - Number(mins(b) < nowMin - 30) || mins(a) - mins(b));
  const mustCount = red.length + redTasks.length;
  const dueCount = amberAll.length + amberTasks.length;

  const sc = score.data;
  const pct = sc?.score != null ? Math.round(Number(sc.score) * 100) : null;

  // "Done" asks what happened and what's next: saving the log closes the follow-up
  function done(i: CCItem) {
    if (i.kind !== "follow_up" || !i.person_id) return;
    log({ person_id: i.person_id, person_name: i.title, area: (["clients", "team"].includes(i.area) || LEAD_AREAS.includes(i.area as Area) ? i.area : undefined) as Area | undefined,
          follow_up_id: i.item_key.split(":")[1] });
  }
  async function commitment(i: CCItem, s: "Done" | "Missed") {
    await db.update("activities", { id: eq(i.item_key.split(":")[1]) }, { commitment_status: s });
    invalidate("cc"); invalidate("team"); toast(s === "Done" ? "Marked kept" : "Marked broken");
  }
  const open = (i: CCItem) => (i.link ? navigate(i.link) : i.person_id ? openPerson(i.person_id) : undefined);

  const row = (i: CCItem) => (
    <li key={i.item_key} className={clsx("cc-item", `lv-${i.level}`)}>
      <button className="cc-main" onClick={() => open(i)}>
        <span className="cc-area">{AREA[i.area] ?? i.area}</span>
        <span className="cc-title">{i.title}</span>
        <span className="cc-why">{i.why}</span>
        {(i.escalated || (scope === "all" && i.owner_user_id !== me)) && i.owner_name && (
          <span className={clsx("cc-owner", i.escalated && "esc")}>{i.escalated ? `Escalated · ${i.owner_name}'s, ${i.days_late} days late` : `Owner: ${i.owner_name}`}</span>
        )}
      </button>
      <div className="cc-side">
        {i.kind !== "summary" && i.kind !== "birthday" && i.kind !== "meeting" && <Late n={i.days_late} />}
        {i.kind === "pipeline" && i.item_key.startsWith("opp:") && can("pipelines", "edit") && <button className="btn small primary" onClick={() => setNextFor(i.item_key.split(":")[1])}>Set next step</button>}
        {canDo(i) && can("activities", "edit") && <button className={clsx("btn small", i.kind === "pipeline" ? "ghost" : "primary")} onClick={() => setDoIt(i)}>{i.kind === "licensing" ? "Send reminder" : "Do it"}</button>}
        {i.kind === "licensing" && can("team", "edit") && !/^Final mark/.test(i.why) && <ExtendedButton memberId={i.item_key.split(":")[1]} mark={i.due} />}
        {i.kind === "follow_up" && <button className="btn small" title="Log what happened and the next step; that closes it" onClick={() => done(i)}>Done…</button>}
        {i.kind === "commitment" && <><button className="btn small" onClick={() => commitment(i, "Done")}>Kept</button><button className="btn small ghost" onClick={() => commitment(i, "Missed")}>Broken</button></>}
        {can("tasks", "edit") && i.kind !== "meeting" && <button className="btn small ghost" title="Make this a task and give it to someone" onClick={() => { setTaskFrom(i); setOpenTask("new"); }}>+ Task</button>}
      </div>
    </li>
  );

  const hello = useMemo(() => { const h = dayjs().hour(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; }, []);
  // The Today list: priorities (the worst must-dos first), what's due today, what was carried over
  type Row = { key: string; item?: CCItem; task?: Task; late: number };
  const lists: Record<typeof tab, Row[]> = {
    priorities: [...red.map((i) => ({ key: i.item_key, item: i, late: i.days_late ?? 0 })), ...redTasks.map((x) => ({ key: `t:${x.id}`, task: x, late: lateBy(x) ?? 0 }))]
      .sort((a, b) => b.late - a.late),
    due: [...amberTasks.map((x) => ({ key: `t:${x.id}`, task: x, late: lateBy(x) ?? 0 })), ...amberAll.map((i) => ({ key: i.item_key, item: i, late: i.days_late ?? 0 }))],
    carried: debts.map((i) => ({ key: i.item_key, item: i, late: i.days_late ?? 0 })),
  };
  const cur = lists[tab];
  const shown = full ? cur : cur.slice(0, TOP);
  const pickTab = (k: typeof tab) => { setTab(k); setFull(false); };
  // only today's birthdays routed to me (Who does what decides)
  const bdayIds = new Set(birthdays.map((i) => i.person_id));
  const myBdays = (bdays.data ?? []).filter((b) => b.days_away === 0 && bdayIds.has(b.person_id));
  const cap = <T,>(k: string, list: T[]) => (more[k] ? list : list.slice(0, SIDE));
  const showAll = (k: string, n: number, label = "Show all") => n > SIDE && (
    <div className="panel-body cc-more"><button className="linkish small" onClick={() => setMore((m) => ({ ...m, [k]: !m[k] }))}>{more[k] ? "Show fewer" : `${label} ${n} →`}</button></div>);

  return (
    <>
      <PageHead help="day" title="Command Center" sub="Everything that needs action, from every part of the CRM. Clear the red first. Late things get louder until they're done."
        actions={boss && <Seg value={scope} onChange={(v) => setQuery({ scope: v === "mine" ? null : v })} options={[{ value: "mine", label: "My list" }, { value: "all", label: "Everyone's" }]} />} />
      <section className="cc-head">
        <div className="cc-hello">
          <span className="date">{dayjs().format("dddd, MMMM D")}</span>
          <span className="focus">{hello}, {profile?.display_name?.split(" ")[0]}.</span>
          <div className="who">{cc.error && !cc.data ? "Your list didn't load" : <><b>{mustCount}</b> must-do{mustCount === 1 ? "" : "s"}</>} · {dueCount} due today · {meetings.length} meeting{meetings.length === 1 ? "" : "s"}
            {can("activities") && <> · <button className="linkish cc-loglink" onClick={() => navigate("/reports?view=log&range=today")}>{loggedToday ?? 0} logged today →</button></>}</div>
        </div>
        {sc ? <TargetDials score={sc} /> : <div />}
        <div className="cc-score">
          {pct != null && <div><span className="v">{pct}%</span><span className="l">today's targets</span></div>}
        </div>
      </section>
      <ErrorBox error={cc.error || tasks.error} />

      <div className="today-cols">
        <div style={{ display: "grid", gap: 14, alignContent: "start" }}>
          <GxPanel />
          <section className="panel cc-today">
            <div className="cc-today-h">
              <h2>Today</h2>
              <span className="small muted">{tab === "priorities" ? (mustCount ? `${Math.min(TOP, mustCount)} priorit${Math.min(TOP, mustCount) === 1 ? "y" : "ies"} from ${mustCount} must-do${mustCount === 1 ? "" : "s"}` : "Nothing late. Good.")
                : tab === "due" ? `${dueCount} due today` : `${debts.length} carried over`}</span>
            </div>
            <div className="cc-tabs" role="tablist">
              {([["priorities", "Priorities", mustCount], ["due", "Due today", dueCount], ["carried", "Carried", debts.length]] as const).map(([k, l, n]) => (
                <button key={k} role="tab" aria-selected={tab === k} className={clsx(tab === k && "on")} onClick={() => pickTab(k)}>{l} ({n})</button>
              ))}
            </div>
            {leadContacts.length > 0 && can("leads") && (
              <div className="cc-leadlink small">
                <span><b>{leadContacts.length}</b> lead follow-up{leadContacts.length === 1 ? "" : "s"}{leadLate ? ` (${leadLate} late)` : ""} {leadContacts.length === 1 ? "is" : "are"} on Leads today, with everyone else to contact.</span>
                <button className="btn small" onClick={() => navigate("/leads")}>Open Leads today →</button>
              </div>)}
            <p className="cc-tabhint small muted">{tab === "priorities" ? "Late things, worst first: a promised follow-up, a review or deal step past its date, an overdue task."
              : tab === "due" ? "Dated today: not late yet. Clear them before they turn red." : "Yesterday's unfinished must-dos, kept so they aren't wiped at midnight."}</p>
            {cc.loading && !cc.data ? <Loading rows={4} /> : cc.error && !cc.data
              ? <div className="panel-body small" style={{ color: "var(--alert)" }}>Couldn't load your list just now. <button className="linkish" onClick={() => cc.reload()}>Try again</button></div>
              : cur.length === 0 ? <div className="panel-body small muted">{tab === "priorities" ? "Nothing late. Good." : tab === "due" ? "Nothing else due today." : "Nothing carried over."}</div>
              : <ul className="cc-list">{shown.map((r) => r.item ? row(r.item)
                  : <li key={r.key} className="cc-taskrow"><TaskRow t={r.task!} onOpen={setOpenTask} people={people.data} me={me} /></li>)}</ul>}
            {cur.length > TOP && <div className="panel-body cc-more"><button className="linkish" onClick={() => setFull(!full)}>{full ? "Show only the top 5" : `View all ${cur.length} →`}</button></div>}
          </section>
          {summaries.length > 0 && (
            <Panel title="Backlogs to work down" hint="Chip away daily; they shrink as you log">
              <ul className="cc-list">{cap("sum", summaries).map(row)}</ul>
              {showAll("sum", summaries.length)}
            </Panel>
          )}
        </div>

        <aside style={{ display: "grid", gap: 14, alignContent: "start" }}>
          <GoalsPanel extra={<ThisWeek bare />} />
          <Panel title="Today's meetings" hint={meetings.length ? `${meetings.length} today` : undefined}
            actions={can("activities") && <><BookMeetingButton className="btn small" label="Book" /><button className="btn small ghost" onClick={() => navigate("/leads/meetings")}>All</button></>}>
            {meetings.length === 0 ? <div className="panel-body small muted">No meetings today.</div> : <ul className="cc-list">{cap("meet", meetings).map(row)}</ul>}
            {showAll("meet", meetings.length)}
          </Panel>
          <Panel title="Birthdays today" hint={myBdays.length ? `${myBdays.length} to wish` : undefined}
            actions={<button className="btn small ghost" onClick={() => navigate("/birthdays")}>All</button>}>
            {myBdays.length === 0 ? <div className="panel-body small muted">No birthdays today.</div>
              : <ul className="bday-list cc-bdays">{cap("bday", myBdays).map((b) => <BdayRow key={b.person_id} b={b} />)}</ul>}
            {showAll("bday", myBdays.length)}
          </Panel>
          <Panel title="My tasks" hint={laterTasks.length ? `${laterTasks.length} coming up` : undefined}
            actions={can("tasks", "edit") && <button className="btn small" onClick={() => { setTaskFrom(null); setOpenTask("new"); }}>New task</button>}>
            {laterTasks.length === 0 ? <div className="panel-body small muted">Nothing else scheduled.</div>
              : <div className="task-list2">{cap("tasks", laterTasks).map((x) => <TaskRow key={x.id} t={x} onOpen={setOpenTask} people={people.data} me={me} compact />)}</div>}
            <div className="panel-body cc-more">{laterTasks.length > SIDE && <button className="linkish small" onClick={() => setMore((m) => ({ ...m, tasks: !m.tasks }))}>{more.tasks ? "Show fewer" : `Show all ${laterTasks.length} →`}</button>}
              <button className="linkish small" style={{ marginLeft: "auto" }} onClick={() => navigate("/tasks")}>Tasks page</button></div>
          </Panel>
        </aside>
      </div>

      {doIt && <DoItSheet item={doIt} onClose={() => setDoIt(null)} />}
      {nextFor && <NextStepSheet oppId={nextFor} onClose={() => setNextFor(null)} />}
      {openTask && <TaskSheet task={openTask === "new" ? null : openTask} onClose={() => { setOpenTask(null); setTaskFrom(null); }}
        preset={taskFrom ? { title: `${taskFrom.title}: ${taskFrom.why}`.slice(0, 140), person_id: taskFrom.person_id, person_name: taskFrom.person_id ? taskFrom.title : undefined,
          due_on: today, priority: taskFrom.level === "red" ? "High" : "Normal" } : undefined} />}
    </>
  );
}

/** A thin bar on every page when something has been late for a week or more: names the items, and each one opens. */
export function LateBanner() {
  const cc = useCommandCenter();
  const tasks = useMyTasks();
  const [open, setOpen] = useState(false);
  const items = [
    ...(cc.data ?? []).filter((i) => (i.days_late ?? 0) >= 7 && i.kind !== "summary")
      .map((i) => ({ key: i.item_key, title: i.title, why: i.why, late: i.days_late ?? 0, go: () => (i.link ? navigate(i.link) : i.person_id ? openPerson(i.person_id) : navigate("/command")) })),
    ...(tasks.data ?? []).filter((x) => (lateBy(x) ?? 0) >= 7)
      .map((x) => ({ key: `task:${x.id}`, title: x.title, why: "Task", late: lateBy(x) ?? 0, go: () => navigate("/tasks") })),
  ].sort((a, b) => b.late - a.late);
  if (!items.length) return null;
  const names = items.slice(0, 3).map((i) => i.title).join(", ") + (items.length > 3 ? ` and ${items.length - 3} more` : "");
  return (
    <div className="late-wrap">
      <button className="late-banner" aria-expanded={open} onClick={() => setOpen(!open)}>
        <b>{items.length}</b> thing{items.length > 1 ? "s have" : " has"} been late for a week or more: {names}. {open ? "Hide ▲" : "Show ▼"}
      </button>
      {open && (
        <ul className="late-list">
          {items.map((i) => (
            <li key={i.key}><button onClick={() => { setOpen(false); i.go(); }}>
              <b>{i.title}</b> <span className="muted">· {i.why}</span> <span className="pill alert">{i.late} days late</span>
            </button></li>
          ))}
          <li><button onClick={() => { setOpen(false); navigate("/command"); }}><b>Open the Command Center →</b></button></li>
        </ul>
      )}
    </div>
  );
}
